import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { useSelectorJs } from 'utils/customHooks';
import { useValidateScopes } from 'utils/customHooks/useValidateScopes';
import { useConfirmModal } from 'utils/customHooks/useConfirmModal';
import { getOpParams } from 'utils/getOpParams';
import { RequestLicensesModal } from 'new-components/RequestLicenses/RequestLicensesModal';

import { getVisitorPackageId } from '../../../../constants/packageIds';
import STATUS from '../../../../constants/status';
import { ImmutableRootState } from '../../../../store/store';
import { VisitsState } from '../../../../store/slices/visitsSlice';
import { LocationsState } from '../../../../store/slices/locationsSlice';
import { useVisitorFeature } from '../../../../hooks/useVisitorFeature';

import AccessControlModal from './AccessControlModal';

interface VisitorsDrawerFooterProps {
  setLoading: (loading: boolean) => void;
  loading: boolean;
}

const VisitorsDrawerFooter = ({
  setLoading,
  loading,
}: VisitorsDrawerFooterProps) => {
  const { t } = useTranslation();

  const { orgId } = getOpParams();
  const confirmModal = useConfirmModal();

  const { isValid: hasSigninWrite } = useValidateScopes([
    'o{orgId}-visitorSignIn:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);
  const { isValid: hasSignoutWrite } = useValidateScopes([
    'o{orgId}-visitorSignOut:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);
  const { isValid: hasDenyWrite } = useValidateScopes([
    'o{orgId}-visitorDeny:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);

  const selectedVisit = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visits', 'selectedVisit']),
  ) as VisitsState['selectedVisit'];
  const locations = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'locations']),
  ) as LocationsState['locations'];

  const locationCount = Array.isArray(locations) ? locations.length : 0;

  const { activeUserCount, totalLicenses } = useVisitorFeature(orgId);

  const licensesLeft = totalLicenses - locationCount;

  const [isAccessControlModalOpen, setIsAccessControlModalOpen] =
    useState(false);
  const [newStatus, setNewStatus] = useState<number>(0);
  const [isRequestLicensesModalOpen, setIsRequestLicensesModalOpen] =
    useState(false);

  const hasPendingVisitors = selectedVisit?.visitors.some(
    (visitor) => visitor.status === STATUS.PENDING.id,
  );
  const hasSignedInVisitors = selectedVisit?.visitors.some(
    (visitor) => visitor.status === STATUS.SIGNED_IN.id,
  );

  const checkLicenseAndProceed = (callback: () => void) => {
    if (locationCount > totalLicenses) {
      confirmModal({
        title: t('License limit reached'),
        content: `You have ${Math.max(0, licensesLeft)} licenses remaining.`,
        okText: t('Request licenses'),
        onOk: () => setIsRequestLicensesModalOpen(true),
      });
      return;
    }

    callback();
  };

  return (
    <>
      {hasPendingVisitors && (
        <>
          {hasSigninWrite && (
            <OpButton
              type="primary"
              ghost
              style={{ width: '100%', marginBottom: '10px' }}
              onClick={() => {
                checkLicenseAndProceed(() => {
                  setIsAccessControlModalOpen(true);
                  setNewStatus(Number(STATUS.SIGNED_IN.id));
                });
              }}
              disabled={loading}
            >
              {t('visitors.footer.signIn')}
            </OpButton>
          )}
          {hasDenyWrite && (
            <OpButton
              type="primary"
              danger
              ghost
              style={{ width: '100%', marginBottom: '10px' }}
              onClick={() => {
                checkLicenseAndProceed(() => {
                  setIsAccessControlModalOpen(true);
                  setNewStatus(Number(STATUS.DENIED_ENTRY.id));
                });
              }}
              disabled={loading}
            >
              {t('visitors.footer.denyEntry')}
            </OpButton>
          )}
        </>
      )}
      {hasSignedInVisitors && hasSignoutWrite && (
        <OpButton
          type="primary"
          ghost
          style={{ width: '100%' }}
          onClick={() => {
            checkLicenseAndProceed(() => {
              setIsAccessControlModalOpen(true);
              setNewStatus(Number(STATUS.SIGNED_OUT.id));
            });
          }}
          testId="select-visitors-to-sign-out-button"
          disabled={loading}
        >
          {t('visitors.footer.signOut')}
        </OpButton>
      )}
      {isAccessControlModalOpen && (
        <AccessControlModal
          open={isAccessControlModalOpen}
          newStatus={newStatus}
          onClose={() => setIsAccessControlModalOpen(false)}
          setLoading={setLoading}
        />
      )}
      {isRequestLicensesModalOpen && (
        <RequestLicensesModal
          orgId={orgId}
          featureName={t('Visitor')}
          modalOpen={isRequestLicensesModalOpen}
          onClose={() => setIsRequestLicensesModalOpen(false)}
          packageId={getVisitorPackageId(activeUserCount)}
        />
      )}
    </>
  );
};

export default VisitorsDrawerFooter;
